/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getUrlSync, IComponent, IComponentProps } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { format, getPayloadObject, ITelemetryContent, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import { observer } from 'mobx-react';
import * as React from 'react';

export interface ICustomCartIconData {
    cart?: ICartState;
}

export interface ICustomCartIconComponentProps extends IComponentProps<ICustomCartIconData> {
    className?: string;
    cartLabel: string;
    cartQtyLabel: string;
    telemetryContent?: ITelemetryContent;
}

export interface ICustomCartIconComponent extends IComponent<ICustomCartIconComponentProps> {}

/**
 *
 * CustomCartIcon component.
 * @extends {React.PureComponent<ICustomCartIconProps>}
 */

@observer
export class CustomCartIcon extends React.PureComponent<ICustomCartIconComponentProps> {
    constructor(props: ICustomCartIconComponentProps) {
        super(props);
    }

    public render(): JSX.Element {
        const {
            cartLabel,
            cartQtyLabel,
            data: { cart }
        } = this.props;
        const cartLink = getUrlSync('cart', this.props.context.actionContext);

        // If there are invoice lines, don't update mini cart item quantity
        const cartItem = cart ? cart.totalItemsInCart : 0;
        const qtyLabel = format(cartQtyLabel, cartItem);
        const itemCount = qtyLabel.replace('(', '');
        const itemCountNew = itemCount.replace(')', '');
        const label = format(cartLabel, cartItem);

        // Construct telemetry attribute to render
        const payLoad = getPayloadObject('click', this.props.telemetryContent!, 'cart-icon', '');
        //const attributes = getTelemetryAttributes(this.props.telemetryContent!, payLoad);
        const style: React.CSSProperties = {
            visibility: cart ? 'visible' : 'hidden'
        };
        return (
            <>
                {Number(itemCountNew) && Number(itemCountNew) > 0 ? (
                    <a
                        className='msc-cart-icon msc-btn active-cart-count'
                        href={cartLink}
                        onClick={onTelemetryClick(this.props.telemetryContent!, payLoad, '')}
                    >
                        <div className='localy-cart-icon'>
                            <svg width='17' height='17' viewBox='0 0 17 17' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                <path
                                    d='M15.9285 1.07153H13.0714L12.0771 10.9572C12.0358 11.2298 11.8974 11.4782 11.6873 11.6567C11.4773 11.8352 11.2099 11.9318 10.9343 11.9287H3.73426C3.48504 11.9417 3.23841 11.8728 3.03205 11.7324C2.82568 11.5921 2.67092 11.3881 2.5914 11.1515L1.0714 6.5801C1.01473 6.40824 0.999681 6.22537 1.0275 6.04655C1.05531 5.86772 1.1252 5.69807 1.2314 5.55153C1.34204 5.39574 1.48997 5.27017 1.66168 5.18632C1.83338 5.10246 2.02336 5.063 2.21426 5.07153H12.6714'
                                    stroke='white'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                />
                                <path
                                    d='M3.92861 15.9287C4.2442 15.9287 4.50003 15.6729 4.50003 15.3573C4.50003 15.0417 4.2442 14.7859 3.92861 14.7859C3.61301 14.7859 3.35718 15.0417 3.35718 15.3573C3.35718 15.6729 3.61301 15.9287 3.92861 15.9287Z'
                                    stroke='white'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                />
                                <path
                                    d='M11.3571 15.9287C11.6727 15.9287 11.9285 15.6729 11.9285 15.3573C11.9285 15.0417 11.6727 14.7859 11.3571 14.7859C11.0415 14.7859 10.7856 15.0417 10.7856 15.3573C10.7856 15.6729 11.0415 15.9287 11.3571 15.9287Z'
                                    stroke='white'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                />
                            </svg>
                        </div>
                        <div className='cart-quantity' aria-hidden style={style}>
                            {itemCountNew} &nbsp;Items
                        </div>
                    </a>
                ) : (
                    <>
                        <a
                            className='msc-cart-icon msc-btn active-cart-count'
                            href={cartLink}
                            aria-label={label}
                            onClick={onTelemetryClick(this.props.telemetryContent!, payLoad, '')}
                        >
                            Cart
                            <div className='localy-cart-icon'>
                                <svg width='17' height='17' viewBox='0 0 17 17' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                    <path
                                        d='M15.9285 1.07153H13.0714L12.0771 10.9572C12.0358 11.2298 11.8974 11.4782 11.6873 11.6567C11.4773 11.8352 11.2099 11.9318 10.9343 11.9287H3.73426C3.48504 11.9417 3.23841 11.8728 3.03205 11.7324C2.82568 11.5921 2.67092 11.3881 2.5914 11.1515L1.0714 6.5801C1.01473 6.40824 0.999681 6.22537 1.0275 6.04655C1.05531 5.86772 1.1252 5.69807 1.2314 5.55153C1.34204 5.39574 1.48997 5.27017 1.66168 5.18632C1.83338 5.10246 2.02336 5.063 2.21426 5.07153H12.6714'
                                        stroke='white'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                    />
                                    <path
                                        d='M3.92861 15.9287C4.2442 15.9287 4.50003 15.6729 4.50003 15.3573C4.50003 15.0417 4.2442 14.7859 3.92861 14.7859C3.61301 14.7859 3.35718 15.0417 3.35718 15.3573C3.35718 15.6729 3.61301 15.9287 3.92861 15.9287Z'
                                        stroke='white'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                    />
                                    <path
                                        d='M11.3571 15.9287C11.6727 15.9287 11.9285 15.6729 11.9285 15.3573C11.9285 15.0417 11.6727 14.7859 11.3571 14.7859C11.0415 14.7859 10.7856 15.0417 10.7856 15.3573C10.7856 15.6729 11.0415 15.9287 11.3571 15.9287Z'
                                        stroke='white'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                    />
                                </svg>
                            </div>
                        </a>
                    </>
                )}
            </>
        );
    }
}
